<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { AppController } from '../app/controller.svelte';
  import { installContentKinds } from '../app/content';
  import { missingRequirements } from '../platform/capabilities';
  import Calibration from './components/Calibration.svelte';
  import HelpOverlay from './components/HelpOverlay.svelte';
  import Hud from './components/Hud.svelte';
  import Panel from './components/Panel.svelte';
  import LivePane from './components/LivePane.svelte';
  import Notices from './components/Notices.svelte';
  import PresetStrip from './components/PresetStrip.svelte';
  import PreviewBadges from './components/PreviewBadges.svelte';
  import PreviewBar from './components/PreviewBar.svelte';
  import SourcePrompt from './components/SourcePrompt.svelte';
  import TourOverlay from './components/TourOverlay.svelte';
  import WelcomeDialog from './components/WelcomeDialog.svelte';
  import { keepAwake, toggleFullscreen } from './display';
  import { watchGestures, type Gesture } from './gestures';

  const ctl = new AppController();
  installContentKinds(ctl);
  const missing = missingRequirements(ctl.caps);

  let stage = $state<HTMLDivElement>();
  let canvas = $state<HTMLCanvasElement>();
  let idle = $state(false);
  let dragging = $state(false);
  let calibrating = $state(false);
  let idleTimer: ReturnType<typeof setTimeout> | undefined;

  function wake() {
    idle = false;
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      if (ctl.started) idle = true;
    }, ctl.settings.hideControlsAfterMs);
  }

  async function onDrop(e: DragEvent) {
    e.preventDefault();
    dragging = false;
    const files = await droppedFiles(e.dataTransfer);
    const audio = files.filter(
      (f) => f.type.startsWith('audio/') || /\.(mp3|wav|ogg|flac|m4a|aac|opus)$/i.test(f.name),
    );
    const other = files.filter((f) => !audio.includes(f));
    if (other.length) await ctl.importFiles(other);
    if (audio.length) await ctl.playFiles(audio);
  }

  /** Files from a drop, including the contents of dropped folders. */
  async function droppedFiles(dt: DataTransfer | null): Promise<File[]> {
    if (!dt) return [];
    const entries = [...dt.items]
      .map((i) => i.webkitGetAsEntry?.())
      .filter((x): x is FileSystemEntry => !!x);
    if (entries.length === 0) return [...dt.files];
    const out: File[] = [];
    const walk = async (entry: FileSystemEntry): Promise<void> => {
      if (entry.isFile) {
        out.push(
          await new Promise<File>((res, rej) => (entry as FileSystemFileEntry).file(res, rej)),
        );
      } else if (entry.isDirectory) {
        const reader = (entry as FileSystemDirectoryEntry).createReader();
        let batch: FileSystemEntry[];
        do {
          batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
          for (const child of batch) await walk(child);
        } while (batch.length > 0);
      }
    };
    for (const e of entries) await walk(e);
    return out;
  }

  // Test/debug introspection (read-only snapshot; never used by the app itself).
  $effect(() => {
    void [
      ctl.stats,
      ctl.transport,
      ctl.scene,
      ctl.source,
      ctl.bank,
      ctl.currentPresetId,
      ctl.outputsVersion,
      ctl.blackout,
      ctl.freeze,
      ctl.grid,
      ctl.recentErrors,
      ctl.previewMode,
      ctl.program,
    ];
    (window as unknown as { __glow: unknown; __glowCtl: unknown }).__glow = ctl.debugSnapshot();
  });
  (window as unknown as { __glowCtl: unknown }).__glowCtl = ctl;

  /** Touch gestures on the visuals (phones, tablets, the installed app). */
  function onGesture(g: Gesture) {
    if (g === 'swipe-left') ctl.stepFavorite(1);
    else if (g === 'swipe-right') ctl.stepFavorite(-1);
    else if (g === 'swipe-up') ctl.panelOpen = true;
    else if (g === 'swipe-down') ctl.surprise();
    else void toggleFullscreen();
  }

  let resizeObserver: ResizeObserver | undefined;
  onMount(() => {
    if (stage && canvas) ctl.attach(stage, canvas);
    void ctl.prepare();
    ctl.maybeWelcome();
    resizeObserver = new ResizeObserver(() => ctl.resize());
    if (stage) resizeObserver.observe(stage);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && ctl.started) void keepAwake(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    // Accidental-exit protection (CTL-9): closing or reloading mid-show asks first.
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (ctl.started && (ctl.performance || ctl.outputs.outputs.size > 0)) e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    const stopGestures = canvas ? watchGestures(canvas, onGesture) : undefined;
    return () => {
      stopGestures?.();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  });

  onDestroy(() => {
    resizeObserver?.disconnect();
    ctl.dispose();
  });
</script>

<svelte:window
  onkeydown={(e) => ctl.handleKey(e, 'down')}
  onkeyup={(e) => ctl.handleKey(e, 'up')}
  onpointermove={wake}
  onpointerdown={wake}
/>

{#if missing.length > 0}
  <div class="unsupported" data-testid="unsupported">
    <h1>This browser can’t run Glowtrope</h1>
    <p>It needs {missing.join(', ')}.</p>
    <p>Try a current version of Chrome, Edge, Firefox or Safari.</p>
  </div>
{:else}
  <div
    class="stage"
    class:idle={idle && !ctl.panelOpen}
    bind:this={stage}
    role="application"
    aria-label="Music visualizer"
    ondragover={(e) => {
      e.preventDefault();
      dragging = true;
    }}
    ondragleave={() => (dragging = false)}
    ondrop={onDrop}
  >
    <canvas
      bind:this={canvas}
      data-testid="canvas"
      class:contain={ctl.outputsVersion >= 0 &&
        ctl.outputs.mode === 'tile' &&
        ctl.outputs.outputs.size > 0}
    ></canvas>

    {#if ctl.hudVisible}
      <Hud {ctl} />
    {/if}
    {#if ctl.previewMode}
      <PreviewBadges />
    {/if}
    {#if ctl.panelOpen}
      <Panel {ctl} oncalibrate={() => (calibrating = true)} />
    {:else if !idle && !ctl.performance}
      <button class="show-panel" data-testid="show-panel" onclick={() => (ctl.panelOpen = true)}
        >Controls</button
      >
    {/if}
    {#if ctl.pending && !ctl.performance}
      <div class="pending" data-testid="pending">→ {ctl.pending}</div>
    {/if}

    <!-- Bottom-left dock: notices on top, then the music picker until something plays, the Live
         pane and switcher, then the strip. Everything stacks here so nothing covers anything else. -->
    <div class="dock" class:with-panel={ctl.panelOpen} data-testid="dock">
      <Notices {ctl} />
      {#if !ctl.started && !ctl.welcomeOpen && ctl.tourStep === null}<SourcePrompt {ctl} />{/if}
      {#if ctl.started && (ctl.panes.live || ctl.previewMode)}
        <div class="dock-row">
          {#if ctl.panes.live}<LivePane {ctl} visible={!idle || ctl.previewMode} />{/if}
          {#if ctl.previewMode}<PreviewBar {ctl} />{/if}
        </div>
      {/if}
      {#if ctl.started && ctl.panes.strip}<PresetStrip {ctl} />{/if}
    </div>

    {#if ctl.helpOpen}
      <HelpOverlay {ctl} />
    {/if}

    {#if ctl.welcomeOpen}
      <WelcomeDialog {ctl} />
    {:else if ctl.tourStep !== null}
      <TourOverlay {ctl} />
    {/if}

    {#if calibrating}
      <Calibration
        onclose={() => (calibrating = false)}
        onresult={(ms) => {
          ctl.setSetting('visualLatencyMs', ms);
          ctl.setSetting('syncOffsetMs', 0);
          calibrating = false;
          ctl.toast(`Display latency set to ${ms} ms.`);
        }}
      />
    {/if}

    {#if dragging}
      <div class="drop">Drop audio files or a folder to play · presets and shaders to import</div>
    {/if}
  </div>
{/if}

<style>
  .stage {
    position: fixed;
    inset: 0;
    background: #000;
  }
  .stage.idle {
    cursor: none;
  }
  canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
    /* Swipes and double taps are ours: no browser panning, zooming or swipe navigation. */
    touch-action: none;
    -webkit-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
  }
  canvas.contain {
    object-fit: contain;
  }
  .show-panel {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 10;
    background: var(--panel);
    backdrop-filter: blur(12px);
  }
  .pending {
    position: absolute;
    top: 14px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 11;
    padding: 4px 12px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.6);
    border: 1px solid color-mix(in srgb, var(--accent) 60%, transparent);
    font-size: 12.5px;
  }
  .dock {
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: 12px;
    z-index: 30;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
    pointer-events: none;
  }
  /* Stop short of the control panel on the right. */
  .dock.with-panel {
    right: 460px;
  }
  @media (max-width: 640px) {
    .dock.with-panel {
      right: 12px;
      bottom: calc(62vh + 16px);
    }
  }
  .dock-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }
  .drop {
    position: absolute;
    inset: 16px;
    z-index: 40;
    display: grid;
    place-items: center;
    border: 2px dashed var(--accent);
    border-radius: var(--radius);
    background: rgba(5, 5, 8, 0.7);
    font-size: 18px;
    pointer-events: none;
    text-align: center;
    padding: 16px;
  }
  .unsupported {
    max-width: 520px;
    margin: 15vh auto;
    padding: 0 16px;
    line-height: 1.5;
  }
</style>
